import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Input, Page, StatusBadge, Table, Textarea, useToast } from "@theg1group/ui";
import type { TableColumn } from "@theg1group/ui";
import { type DragEvent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.ts";
import type { FileList, FileRow, UploadResult } from "../lib/types.ts";

export function Files() {
	const qc = useQueryClient();
	const navigate = useNavigate();
	const toast = useToast();
	const [q, setQ] = useState("");
	const [raw, setRaw] = useState("");
	const [dragOver, setDragOver] = useState(false);
	const [showPaste, setShowPaste] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const list = useQuery({
		queryKey: ["files", q],
		queryFn: () => api.get<FileList>(`/api/files?q=${encodeURIComponent(q)}`),
	});

	function announce(results: UploadResult[]) {
		for (const r of results) {
			toast.addToast(
				r.duplicate ? "info" : "success",
				`${r.duplicate ? "Already ingested" : "New case"} — ${r.messageId.slice(0, 8)}… · ${
					r.subject ?? r.filename ?? "email"
				}`,
			);
		}
		qc.invalidateQueries({ queryKey: ["files"] });
	}

	const upload = useMutation({
		mutationFn: async (files: File[]) => {
			const form = new FormData();
			for (const f of files) form.append("files", f, f.name);
			return api.postForm<{ results: UploadResult[] }>("/api/files", form);
		},
		onSuccess: (r) => announce(r.results),
		onError: (e) => toast.addToast("error", String(e)),
	});

	const uploadRaw = useMutation({
		mutationFn: () => api.post<{ results: UploadResult[] }>("/api/files", { raw }),
		onSuccess: (r) => {
			announce(r.results);
			setRaw("");
			setShowPaste(false);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const del = useMutation({
		mutationFn: (id: string) => api.del(`/api/files/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
	});
	const reparse = useMutation({
		mutationFn: (id: string) => api.post(`/api/messages/${id}/reparse`),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
	});

	function onDrop(e: DragEvent) {
		e.preventDefault();
		setDragOver(false);
		const files = [...e.dataTransfer.files].filter(
			(f) => /\.eml$/i.test(f.name) || /message\/rfc822/i.test(f.type),
		);
		if (files.length) upload.mutate(files);
	}

	const columns: TableColumn<FileRow>[] = [
		{
			id: "subject",
			header: "Subject / File",
			accessor: (r) => (
				<button
					type="button"
					className="text-primary hover:underline text-left"
					onClick={() => navigate(`/messages/${r.id}`)}
				>
					{r.subject ?? r.source_filename ?? "(no subject)"}
				</button>
			),
		},
		{ id: "from", header: "From domain", accessor: (r) => r.from_domain ?? "—" },
		{ id: "esp", header: "ESP", accessor: (r) => (r.esp ? <Badge variant="info">{r.esp}</Badge> : "—") },
		{ id: "class", header: "Class", accessor: (r) => r.classification ?? "—" },
		{ id: "landing", header: "Landing", accessor: (r) => r.landing ?? "—" },
		{ id: "status", header: "Status", accessor: (r) => <StatusBadge status={r.parse_status} /> },
		{
			id: "added",
			header: "Added",
			accessor: (r) => new Date(r.uploaded_at).toLocaleDateString(),
		},
		{
			id: "actions",
			header: "",
			accessor: (r) => (
				<div className="flex gap-1.5">
					<Button size="sm" variant="ghost" onClick={() => reparse.mutate(r.id)}>
						Reparse
					</Button>
					<Button size="sm" variant="danger" onClick={() => del.mutate(r.id)}>
						Delete
					</Button>
				</div>
			),
		},
	];

	return (
		<Page
			title="Files / Inbox"
			description="Каждое загруженное письмо получает сквозной UUID и становится кейсом; повторная заливка вернёт тот же UUID."
			actions={
				<Button variant="secondary" onClick={() => setShowPaste((v) => !v)}>
					Paste raw email
				</Button>
			}
		>
			{/* Drop zone */}
			<Card className="mb-4">
				<CardBody>
				{/** biome-ignore lint/a11y/useKeyWithClickEvents: click-to-browse drop zone */}
				<div
					onDragOver={(e) => {
						e.preventDefault();
						setDragOver(true);
					}}
					onDragLeave={() => setDragOver(false)}
					onDrop={onDrop}
					onClick={() => inputRef.current?.click()}
					className={`rounded-md border-2 border-dashed px-4 py-8 text-center cursor-pointer text-sm ${
						dragOver ? "border-primary bg-primary-soft text-primary-soft-fg" : "border-line text-muted"
					}`}
				>
					{upload.isPending
						? "Uploading…"
						: "Drop .eml files here or click to choose (multiple allowed)"}
					<input
						ref={inputRef}
						type="file"
						accept=".eml,message/rfc822"
						multiple
						hidden
						onChange={(e) => {
							const files = [...(e.target.files ?? [])];
							if (files.length) upload.mutate(files);
							e.target.value = "";
						}}
					/>
				</div>

				{showPaste && (
					<div className="mt-3">
						<Textarea
							value={raw}
							onChange={(e) => setRaw(e.target.value)}
							placeholder="Paste full .eml source (headers + body)…"
							className="w-full min-h-[140px] font-mono text-xs"
						/>
						<div className="mt-2">
							<Button
								variant="primary"
								disabled={!raw.trim()}
								loading={uploadRaw.isPending}
								onClick={() => uploadRaw.mutate()}
							>
								Add pasted email
							</Button>
						</div>
					</div>
				)}
				</CardBody>
			</Card>

			<div className="mb-3 max-w-sm">
				<Input
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder="Search subject / domain / sender…"
				/>
			</div>

			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={list.data?.rows ?? []}
						keyExtractor={(r) => r.id}
						loading={list.isLoading}
						emptyMessage="No emails yet — add some above."
					/>
				</div>
			</Card>
		</Page>
	);
}
